import React, { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Column,
  Columns,
  Inline,
  Loader,
  Stack,
  Text,
} from 'braid-design-system';
import { AdvertiserNation } from '../../utils/AdvertiserNation';
import { useAppContext } from '../../context/AppContext';
import { formatMinorUnits } from '@seek/currency';
import { DisplayRecommendation } from 'src/App/generated/graphql-types';
import { AppError } from 'src/App/utils/error';
import { getLogger } from 'src/App/utils/logger/logger';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import type { Locale } from '@seek/melways-sites';
import { TaxWrapper } from './TaxWrapper';
import { usePricingContext } from 'src/App/context/PricingContextProvider';
import { useProductSet } from 'src/hooks';
import { v4 } from 'uuid';
import {
  mapProductSetItems,
  type MappedAdProduct,
} from 'src/mappers/mapProductSetItems';

export interface AdPriceDetailsProps {
  advertiserId?: string;
  roleTitle: string;
  subClassificationId: string;
  locationId: number;
  locationText: string;
  advertiserNation: AdvertiserNation;
  showDiscountedPrice: boolean;
}

const PriceText = ({
  value,
  currency,
  locale,
}: {
  value: number;
  currency: string;
  locale: Locale;
}) => (
  <Text size="standard" weight="strong">
    {formatMinorUnits(value, {
      currency,
      locale,
    })}
  </Text>
);

export const AdPriceDetails = ({
  advertiserId,
  advertiserNation,
  roleTitle,
  subClassificationId,
  locationId,
  locationText,
  showDiscountedPrice = true,
}: AdPriceDetailsProps) => {
  const { t } = useTranslations(translations);
  const { appMode, isPreview } = usePricingContext();
  const [priceBlock, setPriceBlock] = useState<MappedAdProduct[]>([]);
  const { locale } = useAppContext();
  const [showDiscountInfo, setShowDiscountInfo] = useState(false);

  const {
    useProductSetQuery,
    data: productSetData,
    isLoading: productSetLoading,
    error: productSetError,
  } = useProductSet(isPreview);

  useEffect(() => {
    const priceItems: MappedAdProduct[] = mapProductSetItems(
      productSetData?.productSet?.items ?? [],
    );

    const isDiscountedPriceExist = priceItems.some(
      (priceItem) => priceItem.price.discount.discountPercentage > 0,
    );

    const isShowDiscountedPrice =
      showDiscountedPrice && appMode === 'DEFAULT' && isDiscountedPriceExist;

    setPriceBlock(priceItems);
    setShowDiscountInfo(isShowDiscountedPrice);
  }, [productSetData, isPreview]);

  useEffect(() => {
    setShowDiscountInfo(false);
    useProductSetQuery({
      variables: {
        input: {
          advertiserNation,
          jobAdDetails: {
            locationId: locationId.toString(),
            subClassificationId,
            roleTitle,
          },
          opportunityId: v4(),
        },
      },
    });
  }, [locationId, subClassificationId, roleTitle, isPreview]);

  if (productSetLoading) {
    return <Loader delayVisibility={true} />;
  }

  if (productSetError) {
    return (
      <Alert tone="critical">
        <Text>{t('We are currently unable to check prices...')}</Text>
      </Alert>
    );
  }

  const priceRow = (priceBlockItem: MappedAdProduct) => {
    const {
      name,
      price: { netPrice, retailPrice, tax, retailTax },
    } = priceBlockItem;

    const price = showDiscountInfo ? netPrice : retailPrice;
    const priceBeforeDiscount = showDiscountInfo ? retailPrice : null;
    const isSamePrice = netPrice.value === retailPrice.value;

    // optional amount - defaulted to 0
    const isTaxInclusive =
      tax?.displayRecommendation === DisplayRecommendation.INCLUSIVE;
    const priceWithTax = price.value + (tax?.amount.value ?? 0);
    const priceBeforeDiscountWithTax =
      priceBeforeDiscount?.value + (retailTax?.amount.value ?? 0);

    // Handle null scenario
    if (price.value == null) {
      const variables = {
        advertiserId,
        advertiserNation,
        input: {
          roleTitle,
          subClassificationId,
          locationId,
        },
      };
      const nullPriceError = new AppError(
        'NULL_PRICE',
        `Price is null for ad type ${name}`,
      );
      getLogger().error(nullPriceError.message, {
        activity: 'renderPrice',
        error: nullPriceError,
        variables,
      });
    }

    return (
      <Columns space="xsmall" key={name}>
        <Column width="1/3">
          <Text size="standard" id={name} data={{ testid: name }}>
            {name}
          </Text>
        </Column>
        {price.value === 0 ? (
          <Column>
            <Text
              size="standard"
              weight="strong"
              align="right"
              id={`${name}_free`}
            >
              {t('Free')}
            </Text>
          </Column>
        ) : (
          <Column>
            <Inline
              space="small"
              align="right"
              data={{ testid: `${name}_price_container` }}
            >
              {priceBeforeDiscount && !isSamePrice && (
                <Text
                  id={`${name}_price_before_discount`}
                  data={{ testid: `${name}_price_before_discount` }}
                  size="small"
                  component="s"
                >
                  {formatMinorUnits(
                    isTaxInclusive
                      ? priceBeforeDiscountWithTax
                      : priceBeforeDiscount.value,
                    {
                      currency: priceBeforeDiscount.currency,
                      locale,
                    },
                  )}
                </Text>
              )}

              <Inline space="xxsmall" align="right">
                <TaxWrapper locale={locale} tax={tax}>
                  <PriceText
                    value={isTaxInclusive ? priceWithTax : price.value}
                    currency={price.currency}
                    locale={locale}
                  />
                </TaxWrapper>
              </Inline>
            </Inline>
          </Column>
        )}
      </Columns>
    );
  };

  return (
    <Stack space="xlarge">
      <Inline space="xsmall" alignY="center">
        <Box paddingTop="xsmall">
          <Text
            id="price_check_title"
            size="large"
            weight="strong"
            component="h3"
          >
            {t('Ad prices for roleTitle in locationText*', {
              roleTitle,
              locationText,
            })}
          </Text>
        </Box>
        {showDiscountInfo && (
          <Box paddingTop="xsmall">
            <Badge
              id="budget_discount_badge"
              data={{ testid: 'budget_discount_badge' }}
              tone="info"
              bleedY={true}
            >
              {t('Budget discounts applied')}
            </Badge>
          </Box>
        )}
      </Inline>
      <Stack space="gutter">{priceBlock.map(priceRow)}</Stack>
    </Stack>
  );
};
